import {
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    OnInit,
    computed,
    inject,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Button } from '@ntv360/component-pantry';
import { filter } from 'rxjs/operators';

import { AuthService } from '@core/services/auth/auth.service';
import { SVG_ICONS } from '@shared/constants';
import { CopyDirective } from '@shared/directives/copy/copy.directive';
import { LAYOUT_ROUTES, NAVBAR_MENU_ITEMS, NavbarMenuItem } from '../../constants';

type ProfileMenuAction = 'profileSettings' | 'logout';

interface UserTraits {
    data: {
        userId: string;
        organizationId: string;
        roleId: string;
        userName: string;
        emailAddress: string;
        firstName: string;
        lastName: string;
        contactNumber: string;
        lastLoginAt: string;
        roleName: string;
        roleCode: string;
        message: string;
    };
}

interface ProfileMenuItem {
    label: string;
    action: ProfileMenuAction;
}

@Component({
    selector: 'app-navbar',
    standalone: true,
    imports: [Button, CopyDirective, RouterModule],
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
    private readonly profileSettingsRoute = LAYOUT_ROUTES.PROFILE_SETTINGS;
    /**
     * Injected ElementRef for internal DOM access.
     */
    private el = inject(ElementRef);

    /**
     * Injected Router for navigation and URL checks.
     */
    private router = inject(Router);

    /**
     * DestroyRef for automatic subscription cleanup.
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Auth service for handling logout from the profile dropdown.
     */
    private authService = inject(AuthService);

    /**
     * Logged-in user traits, reactive to auth state updates.
     */
    public currentUser = computed(() => this.authService.userTraits() as UserTraits | null);

    /**
     * Reference to the profile menu wrapper, used to detect outside clicks.
     */
    public profileMenuRef = viewChild<ElementRef<HTMLElement>>('profileMenuRef');

    /**
     * Emitted when a menu item or sub-item is selected.
     */
    public selectionChange = output<{ label: string; parent?: string }>();

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file for navbar icons.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * List of menu items to display in the navbar.
     */
    public menuItems = NAVBAR_MENU_ITEMS;

    /**
     * Items rendered inside the profile chevron dropdown.
     */
    public profileMenuItems: ProfileMenuItem[] = [
        { label: 'Profile Settings', action: 'profileSettings' },
        { label: 'Logout', action: 'logout' },
    ];

    /**
     * Whether the profile chevron dropdown is open.
     */
    public showProfileDropdown = false;

    /**
     * Checks if the "Users" option should be hidden for the current user role.
     */
    public isUsersOptionRestricted = computed(() => {
        const role = this.currentUser()?.data?.roleName;
        return role === 'Dealer' || role === 'Dealer Admin';
    });

    /**
     * Checks if the "Dealers" option should be hidden for the current user role.
     */
    public isDealersOptionRestricted = computed(() => {
        const role = this.currentUser()?.data?.roleName;
        return role === 'Dealer';
    });

    /**
     * Two-letter initials derived from the user's first and last name.
     * Falls back to the first available initial or 'U' when names are missing.
     */
    public userInitials = computed(() => {
        const data = this.currentUser()?.data;
        const first = data?.firstName?.trim()?.[0] ?? '';
        const last = data?.lastName?.trim()?.[0] ?? '';
        const initials = `${first}${last}`.toUpperCase();
        return initials || 'U';
    });

    /**
     * Full display name combining first and last name, trimmed.
     */
    public userFullName = computed(() => {
        const data = this.currentUser()?.data;
        const first = data?.firstName?.trim() ?? '';
        const last = data?.lastName?.trim() ?? '';
        return `${first} ${last}`.trim() || 'User';
    });

    /**
     * Email address shown in the profile dropdown header.
     */
    public userEmail = computed(() => this.currentUser()?.data?.emailAddress ?? '');

    /**
     * Role label shown alongside the user's name.
     */
    public userRoleName = computed(() => this.currentUser()?.data?.roleName ?? '');

    /**
     * State of the mobile menu.
     */
    public isMenuOpen = false;

    /**
     * Current router URL, kept reactive so active-link highlighting recomputes
     * on every navigation — including programmatic redirects (e.g. dealer guards).
     */
    private currentUrl = signal<string>(this.router.url);

    /**
     * Closes the mobile menu on every completed navigation and syncs the URL signal.
     */
    public ngOnInit(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                this.isMenuOpen = false;
                this.currentUrl.set((event as NavigationEnd).urlAfterRedirects);
            });
    }

    /**
     * Toggles the mobile menu.
     */
    public toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen;
    }

    /**
     * Closes the profile dropdown when clicking outside its container.
     * @param event The mouse event.
     */
    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node;

        if (!this.el.nativeElement.contains(target)) {
            this.showProfileDropdown = false;
            return;
        }

        const profileEl = this.profileMenuRef()?.nativeElement;
        if (profileEl && !profileEl.contains(target)) {
            this.showProfileDropdown = false;
        }
    }

    /**
     * Toggles the profile chevron dropdown.
     * @param event The mouse event for propagation stopping.
     */
    public toggleProfileDropdown(event: Event): void {
        event.stopPropagation();
        this.showProfileDropdown = !this.showProfileDropdown;
    }

    /**
     * Handles selection of a profile dropdown item.
     * Logout calls the AuthService; Profile and Settings are placeholders
     * until their respective routes are implemented.
     * @param action The action identifier of the selected item.
     */
    public onProfileMenuItem(action: ProfileMenuAction): void {
        this.showProfileDropdown = false;

        if (action === 'logout') {
            this.authService.logout().subscribe();
            return;
        }

        if (action === 'profileSettings') {
            this.router.navigate([this.profileSettingsRoute]);
        }
    }

    /**
     * Opens profile settings from quick action icon.
     */
    public openProfileSettings(): void {
        this.router.navigate([this.profileSettingsRoute]);
    }

    /**
     * Sets the clicked menu item as active and deactivates others.
     * @param selectedItem The menu item to activate.
     */
    public setActive(selectedItem: NavbarMenuItem): void {
        this.menuItems.forEach((item) => (item.active = false));
        selectedItem.active = true;
        this.selectionChange.emit({ label: selectedItem.label });
        if (selectedItem.externalUrl) {
            window.open(selectedItem.externalUrl, '_blank');
            return;
        }
        if (selectedItem.route) {
            this.router.navigate([selectedItem.route]);
        }
    }

    /**
     * Checks if a menu item is active based on the current URL.
     * Returns true if the URL matches the item's own route.
     */
    public isRouteActive(item: NavbarMenuItem): boolean {
        const url = this.currentUrl();
        const isProfileSettingsPage = url.startsWith(this.profileSettingsRoute);
        if (isProfileSettingsPage) {
            // URL is under `/users/...` but this screen is account settings, not the Users module.
            // Highlight only Dashboard so the Users nav item does not appear active.
            return item.label === 'Dashboard';
        }

        if (item.route && (url === item.route || url.startsWith(item.route + '/'))) {
            return true;
        }
        return false;
    }
}
